import React, { useEffect } from "react";
import { AiFillCheckCircle, AiFillCloseCircle, AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  Col,
  Row,
  UiBox,
  UiCenter,
  UiFlex,
  UiHStack,
  UiIcon,
  UiImage,
  UiPrimaryButton,
  UiStackDivider,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";
import { PAGES } from "~/constants";
import { UPLOAD_TIPS_BAD_PHOTOS, UPLOAD_TIPS_GOOD_PHOTOS, UploadTips } from "~/constants/dashboard";
import { useAppDispatch } from "~/hooks";
import { EDashboardStep, setActiveStep, setDashboardProgress } from "~/redux/dashboards/slice";
import { RootState } from "~/redux/root-reducer";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { DashboardStepper } from "~/components/shared/DashboardStepper";
import dashboardImages from "~/pages/dashboard/assets";
import { useHistory } from "react-router-dom";

interface TipBoxProps {
  images: UploadTips["images"];
  tips: UploadTips["content"];
  type: "bad" | "good";
}

export function TipBox({ images, tips, type }: TipBoxProps) {
  return (
    <Col p={{ base: 4, lg: 6 }} bg={type === "bad" ? "#FFF1EB" : "lightgreen"} borderRadius="1.25rem" flex={1}>
      <Row alignItems="center">
        <UiIcon
          as={type === "bad" ? AiFillCloseCircle : AiFillCheckCircle}
          boxSize={6}
          color={type === "bad" ? "red" : "green"}
          mx={0}
        />
        <UiText vr="title1" ml={2}>
          {type === "bad" ? "Bad Photos Uploads" : "Good Photos Uploads"}
        </UiText>
      </Row>
      <UiHStack mt={{ base: 4, lg: 8 }} gap={{ base: 2.5, lg: 4 }}>
        {images.map(i => (
          <UiImage
            key={i.id}
            src={i.src}
            borderRadius="0.5rem"
            maxW="7rem"
            maxH="7rem"
            aspectRatio={1}
            w="full"
            containerClasses="flex1 w-full aspect-square center"
          />
        ))}
      </UiHStack>
      <UiVStack
        gap={{ base: 2, lg: 3 }}
        mt={{ base: 4, lg: 6 }}
        alignItems="flex-start"
        divider={
          <UiStackDivider
            borderBottomWidth="0.5px !important"
            borderColor={type === "bad" ? "neutral.gray00" : "neutral.gray100"}
            my="0 !important"
            ml="2rem !important"
          />
        }
      >
        {tips.map(i => (
          <Row key={i.id} alignItems="center">
            <UiIcon
              as={type === "bad" ? AiOutlineCloseCircle : AiOutlineCheckCircle}
              mx={0}
              boxSize={5}
              color={type === "bad" ? "red" : "green"}
            />
            <UiText vr="body2" ml={3} color="neutral.gray700">
              {i.desc}
            </UiText>
          </Row>
        ))}
      </UiVStack>
    </Col>
  );
}

export function TipNotice({ bg }: { bg?: string }) {
  return (
    <Row p={{ base: 4, lg: 6 }} bg={bg ?? "neutral.gray00"} borderRadius="1.25rem" mt={{ base: 4, lg: 5 }} w="full">
      <UiImage src={dashboardImages.notice} boxSize={6} />
      <Col flex={1} ml={2}>
        <UiText vr="title2">Notice</UiText>
        <UiText vr="body2" color="neutral.gray600" mt={1}>
          Bad photos = bad results
        </UiText>
        <UiText vr="body2" color="neutral.gray600" mt={1}>
          Avoid photos from same shot/ with same background
        </UiText>
      </Col>
    </Row>
  );
}

export function UploadTipsPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { screenIndex } = useSelector((state: RootState) => state.dashboard.dashboardProgress);
  const vrTitle = useBreakpointValue<any>({ base: "headline4", md: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });

  useEffect(() => {
    dispatch(setActiveStep({ step: 3 }));
  }, [dispatch]);

  const onClickNext = async () => {
    dispatch(setDashboardProgress({ step: EDashboardStep.Upload, screenIndex: screenIndex + 1 }));
    history.push(PAGES.UPLOAD_PHOTOS);
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Upload tips`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep
    >
      <Col flex={1} justify="center">
        <UiCenter p={4} gap={5} flexDirection={{ base: "column-reverse", lg: "row" }}>
          <DashboardMainBox>
            <Col>
              <UiBox w="full">
                <UiText vr={vrTitle} textAlign="center">
                  Important Information to Create Great Photoshoots
                </UiText>
                <UiText vr={vrCaption} mt={{ base: 2, sm: 3 }} textAlign="center">
                  Carefully read the photo recommendations below to ensure that you get the best photoshoots.
                </UiText>
              </UiBox>
              <UiFlex
                flexDirection={{ base: "column-reverse", lg: "row" }}
                gap={{ base: 2, lg: 5 }}
                mt={{ base: 4, lg: 8 }}
              >
                <TipBox images={UPLOAD_TIPS_BAD_PHOTOS.images} tips={UPLOAD_TIPS_BAD_PHOTOS.content} type="bad" />
                <TipBox images={UPLOAD_TIPS_GOOD_PHOTOS.images} tips={UPLOAD_TIPS_GOOD_PHOTOS.content} type="good" />
              </UiFlex>
              <TipNotice />
              <UiPrimaryButton
                whiteSpace="normal"
                lineHeight="20px"
                py={3}
                h="auto"
                w="fit-content"
                mx="auto"
                mt={{ base: 2.5, lg: 8 }}
                onClick={onClickNext}
              >
                I understand & will follow the requirements to get great photoshoots!
                <UiIcon hideBelow="sm" as={MdArrowForwardIos} boxSize={5} color="white" />
              </UiPrimaryButton>
            </Col>
          </DashboardMainBox>
          <DashboardStepper />
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
